import * as firebaseAuth from "firebase/auth"
import { app } from "../config/firebase"

const auth = firebaseAuth.getAuth(app);

// Set auth persistence to LOCAL to maintain state across page reloads
firebaseAuth.setPersistence(auth, firebaseAuth.browserLocalPersistence).catch((error) => {
    console.error("Error setting auth persistence:", error);
});

const Auth = () => {
    return {
        getAuth() {
            return auth;
        },
        waitForAuthReady() {
            return auth.authStateReady();
        },
        parseUser(user: firebaseAuth.User) {
            return {
                displayName: user.displayName,
                uid: user.uid,
                photoURL: user.photoURL,
                email: user.email,
                emailVerified: user.emailVerified,
                phoneNumber: user.phoneNumber
            }
        },
        async simpleLogin(email: string, password: string): Promise<firebaseAuth.User> {
            return (await firebaseAuth.signInWithEmailAndPassword(auth, email, password)).user;
        },
        async logout() {
            return await firebaseAuth.signOut(auth);
        },
        async signup(email: string, password: string) {
            return await firebaseAuth.createUserWithEmailAndPassword(auth, email, password)
        },
        async googleOAuthLogin(): Promise<firebaseAuth.User | void> {
            const provider = new firebaseAuth.GoogleAuthProvider();
            if (process.env.NODE_ENV === 'development') {
                const result = await firebaseAuth.signInWithPopup(auth, provider);
                return result.user;
            }
            return await firebaseAuth.signInWithRedirect(auth, provider);
        },
        async handleGoogleRedirectResult() {
            // Call this when your app loads to check if user is returning from Google OAuth
            try {
                const result = await firebaseAuth.getRedirectResult(auth);
                if (result?.user) {
                    return result.user;
                }
                return null;
            } catch (error: any) {
                // Handle specific error cases
                if (error.code === 'auth/account-exists-with-different-credential') {
                    console.error("Account exists with different credential");
                    throw new Error("An account already exists with the same email but different sign-in credentials.");
                }
                console.error("Error getting redirect result:", error);
                throw error;
            }
        },
        async loginWithPhone(phoneNumber: string, appVerifier: firebaseAuth.ApplicationVerifier) {
            return await firebaseAuth.signInWithPhoneNumber(auth, phoneNumber, appVerifier);
        },
        async getCurrentUser(): Promise<firebaseAuth.User | null> {
            return new Promise((resolve, reject) => {
                auth.onAuthStateChanged((user) => {
                    resolve(user);
                }, error => {
                    reject(error)
                })
            })
        },
        async forgotPassword(email: string, url?: string) {
            return url
                ? await firebaseAuth.sendPasswordResetEmail(auth, email, {
                    url,

                })
                : await firebaseAuth.sendPasswordResetEmail(auth, email);
        },
        async resetPassword(code: string, newPassword: string) {
            return await firebaseAuth.confirmPasswordReset(auth, code, newPassword);
        },
        async verifyResetCode(code: string) {
            return await firebaseAuth.verifyPasswordResetCode(auth, code);
        },
        async updateUserProfile(displayName: string) {
            const user = auth.currentUser;
            if (user) {
                return await firebaseAuth.updateProfile(user, { displayName });
            }
            throw new Error("No user is currently logged in.");
        }
    }
}

export default Auth();